import type { vec2 } from 'gl-matrix'
import { mat3 } from 'gl-matrix'

import { assert } from '../../../../guards'
import type {
  HorizonAlignment,
  MsdfChar,
  MsdfConfig,
  MsdfFont,
  Point2D,
  ScaleType,
  Size,
  VerticalAlignment,
} from '../../../../types'
import { getGridCoordinatesToProjectionMatrix } from '../../../../utils'

export const createFontTexture = (renderingContext: WebGL2RenderingContext, font: MsdfFont) => {
  const texture = renderingContext.createTexture()

  assert(texture !== null)

  renderingContext.activeTexture(renderingContext.TEXTURE0)
  renderingContext.bindTexture(renderingContext.TEXTURE_2D, texture)

  renderingContext.texParameteri(
    renderingContext.TEXTURE_2D,
    renderingContext.TEXTURE_WRAP_S,
    renderingContext.CLAMP_TO_EDGE,
  )
  renderingContext.texParameteri(
    renderingContext.TEXTURE_2D,
    renderingContext.TEXTURE_WRAP_T,
    renderingContext.CLAMP_TO_EDGE,
  )
  renderingContext.texParameteri(
    renderingContext.TEXTURE_2D,
    renderingContext.TEXTURE_MIN_FILTER,
    renderingContext.LINEAR,
  )
  renderingContext.texParameteri(
    renderingContext.TEXTURE_2D,
    renderingContext.TEXTURE_MAG_FILTER,
    renderingContext.LINEAR,
  )

  renderingContext.texImage2D(
    renderingContext.TEXTURE_2D,
    0,
    renderingContext.RGBA,
    renderingContext.RGBA,
    renderingContext.UNSIGNED_BYTE,
    font.atlas,
  )

  return texture
}

const getAlignmentOffset = (
  size: Size,
  horizonAlignment: HorizonAlignment,
  verticalAlignment: VerticalAlignment,
): vec2 => {
  let offsetX = 0
  let offsetY = 0
  switch (horizonAlignment) {
    case 'left':
      break
    case 'center':
      offsetX = -size.width / 2
      break
    case 'right':
      offsetX = -size.width
  }
  switch (verticalAlignment) {
    case 'top':
      break
    case 'center':
      offsetY = -size.height / 2
      break
    case 'bottom':
      offsetY = -size.height
  }
  return [offsetX, offsetY]
}

export const createTransformMatrix = (
  canvas: HTMLCanvasElement,
  zoom: ScaleType,
  rotate: number,
  position: Point2D,
  size: Size,
  horizonAlignment: HorizonAlignment,
  verticalAlignment: VerticalAlignment,
  isScaling: boolean,
): mat3 => {
  const projectionMat = getGridCoordinatesToProjectionMatrix(canvas, zoom, rotate, position)
  if (!isScaling) {
    mat3.scale(projectionMat, projectionMat, [1 / zoom.scaleX, 1 / zoom.scaleY])
  }
  const alignMat = mat3.translate(
    mat3.create(),
    projectionMat,
    getAlignmentOffset(size, horizonAlignment, verticalAlignment),
  )
  return alignMat
}

export const getSymbolPositions = (
  char: MsdfChar,
  config: MsdfConfig,
  fontSize: number,
  offset: number,
): Point2D[] => {
  const gliphSize = fontSize / config.info.size
  const x1 = char.xoffset * gliphSize + offset
  const y1 = char.yoffset * gliphSize
  const x2 = (char.xoffset + char.width) * gliphSize + offset
  const y2 = (char.yoffset + char.height) * gliphSize

  return [
    { x: x1, y: y1 },
    { x: x1, y: y2 },
    { x: x2, y: y2 },
    { x: x2, y: y1 },
  ]
}

export const getSymbolTextureCoordinates = (char: MsdfChar, config: MsdfConfig): Point2D[] => {
  const textureX1 = char.x / config.common.scaleW
  const textureY1 = char.y / config.common.scaleH
  const textureX2 = textureX1 + char.width / config.common.scaleW
  const textureY2 = textureY1 + char.height / config.common.scaleH

  return [
    { x: textureX1, y: textureY1 },
    { x: textureX1, y: textureY2 },
    { x: textureX2, y: textureY2 },
    { x: textureX2, y: textureY1 },
  ]
}

export const getChar = (symbol: string | undefined, config: MsdfConfig): MsdfChar | undefined => {
  if (symbol === undefined) return

  return config.chars.find(char => char.char === symbol)
}

export const getKerning = (
  char1: MsdfChar,
  char2: MsdfChar | undefined,
  config: MsdfConfig,
): number => {
  if (!char2) return 0

  const kerning = config.kernings.find(
    kerning => kerning.first === char1.id && kerning.second === char2.id,
  )

  return kerning?.amount ?? 0
}
